import { A, useNavigate, useSearchParams } from '@solidjs/router'
import {
    api_admin_companies_bulk_post,
    api_admin_companies_search_list,
    api_admin_helmet_extension_requests_count_get,
    api_admin_helmet_extension_requests_list,
    api_admin_helmet_extension_requests_patch,
    api_admin_users_bulk_post,
    Company,
    Count,
    HelmetExtensionRequest,
    HelmetExtensionRequestState,
    User,
} from 'abi'
import { addAlert, Counter, HoverInp, LoadingDots, setPopup } from 'components'
import {
    ArrowLeftIcon,
    CalendarIcon,
    CheckFillIcon,
    CloseIcon,
    CorpsIcon,
    EditIcon,
    LoadingIcon,
    QuestionIcon2,
    ShieldCheckIcon,
    UserIcon,
    WarningIcon,
} from 'icons'
import {
    Component,
    createMemo,
    For,
    JSX,
    onCleanup,
    onMount,
    Show,
} from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'
import { isAdmin } from '../users/corp/shared'

import { debounce, deepcopy } from 'utils/tools'
import './style/requests.scss'

export const HelmetState_FA: Record<HelmetExtensionRequestState, string> = {
    pending: 'در انتظار بررسی',
    in_progress: 'در حال انجام',
    rejected: 'رد شده',
    done: 'انجام شده',
}

type REQ = Omit<HelmetExtensionRequest, 'user' | 'company'> & {
    user: User | null
    company: Company | null
}

type STATE = {
    count: Count
    page: number

    company: {
        query: string
        id: number | null
        show: boolean

        results: Company[]
        loading: boolean
        error: boolean
    }
    helmet: number | null

    state: HelmetExtensionRequestState | null

    loadingC: boolean
    loadingR: boolean
    error: boolean

    reqs: REQ[]

    activeReq: REQ | null
}

const STATE_DEF: STATE = {
    page: 0,
    count: {
        max_page: -1,
        total: -1,
    },
    helmet: null,
    company: {
        id: null,
        query: '',
        show: false,

        loading: false,
        error: false,
        results: [],
    },

    state: null,

    loadingC: true,
    loadingR: true,
    error: false,

    reqs: [],
    activeReq: null,
}

const [state, setState] = createStore<STATE>(deepcopy(STATE_DEF))

const ExtentionRequests: Component = () => {
    let nav = useNavigate()

    const [search_params, setSearchParams] = useSearchParams()

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        let p = parseInt(search_params.page || '0')
        if (p !== 0) {
            setState('page', p)
        }

        let page = unwrap(state.page)
        setSearchParams({ page })

        let ac = new AbortController()

        getCount()
        searchReqs()

        window.addEventListener('popstate', handlePopstate, {
            signal: ac.signal,
        })

        onCleanup(() => ac.abort())
    })

    const handlePopstate = () => {
        let page = parseInt(search_params.page || '0')

        setState('page', page)
        searchReqs()
    }

    onCleanup(() => {
        setState(deepcopy(STATE_DEF))
    })

    const getCount = async () => {
        setState('loadingC', true)

        let res = await api_admin_helmet_extension_requests_count_get({})

        setState('loadingC', false)

        if (!res.ok()) return

        setState('count', res.body)
    }

    const searchReqs = async () => {
        let copy = deepcopy(state)

        let s = copy.state
        let cmp = copy.company.id
        let helmet = copy.helmet || null

        let page = copy.page

        setState({
            loadingR: true,
            error: false,
        })

        let res = await api_admin_helmet_extension_requests_list({
            page,
            company: cmp,
            helmet,
            state: s,
        })

        if (!res.ok()) return setState({ loadingR: false, error: true })

        let comps = res.body.map(({ company }) => company)
        let users = res.body.map(({ user }) => user)

        let usersRes = await api_admin_users_bulk_post({ ids: users })
        let compsRes = await api_admin_companies_bulk_post({ ids: comps })

        if (!usersRes.ok() || !compsRes.ok())
            return setState({ loadingR: false, error: true })

        let result: REQ[] = []

        res.body.forEach(r => {
            let uId = usersRes.body.findIndex(u => u.id == r.user)
            let cId = compsRes.body.findIndex(c => c.id == r.company)

            let final: REQ = { ...r, user: null, company: null }

            if (uId !== -1) {
                final['user'] = usersRes.body[uId]!
            }
            if (cId !== -1) {
                final['company'] = compsRes.body[cId]!
            }

            result.push(final)
        })

        setState({ reqs: result, loadingR: false })
    }

    const searchCorps = debounce(async () => {
        let query = unwrap(state.company.query)

        let res = await api_admin_companies_search_list({
            name: `%${query}%`,
        })

        setState('company', { loading: false })

        if (!res.ok() || res.body.length == 0)
            return setState('company', { error: true })

        if (state.company.query !== query) return

        setState('company', {
            error: false,
            results: res.body.slice(0, 5),
        })
    }, 200)

    const CorpPick = () => {
        const pickedCorp = (c: Company) => {
            setState('company', {
                query: c.name,
                id: c.id,
                show: false,
            })
            searchReqs()
        }

        const data = createMemo(() => state.company)

        const Loading = () => {
            return (
                <div class='loading-result title'>
                    <LoadingDots />
                    درحال گرفتن شرکت
                </div>
            )
        }
        const Error = () => {
            return (
                <div class='error-result title'>
                    <CloseIcon />
                    شرکتی یافت نشد!
                </div>
            )
        }

        interface CorpProps extends Company {
            onClick(): void
        }
        const Corp: Component<CorpProps> = C => {
            return (
                <div role='button' class='corp-res' onclick={C.onClick}>
                    <div class='res-head title_small'>{C.name}</div>
                    <div class='res-bottom description'>
                        <div class='data'>{C.agent_count} نماینده</div>•
                        <div class='data'>
                            <CalendarIcon />
                            {new Date(C.created_at * 1e3).toLocaleDateString(
                                'fa-IR'
                            )}
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div class='corp-pick'>
                <HoverInp
                    Icon={<CorpsIcon />}
                    holder='اسم شرکت'
                    acitve={!!state.company.query || state.company.id !== null}
                    inpType='text'
                    onInp={v => {
                        v = v.trim()

                        if (v.length <= 0) {
                            setState('company', deepcopy(STATE_DEF['company']))
                            return
                        }

                        setState('company', {
                            id: null,
                            query: v,
                            show: true,
                        })
                        searchCorps()
                    }}
                    value={state.company.query}
                    showReset={
                        state.company.id !== null || !!state.company.query
                    }
                    onReset={() => {
                        setState('company', deepcopy(STATE_DEF['company']))
                        searchReqs()
                    }}
                />
                <div class='results' classList={{ active: state.company.show }}>
                    <Show when={!data().loading} fallback={<Loading />}>
                        <Show when={!data().error} fallback={<Error />}>
                            <For each={data().results}>
                                {c => (
                                    <Corp
                                        onClick={() => {
                                            pickedCorp(c)
                                        }}
                                        {...c}
                                    />
                                )}
                            </For>
                        </Show>
                    </Show>
                </div>
            </div>
        )
    }

    interface StateFilterProps {
        icon: JSX.Element
        holder: string

        class?: string

        state: HelmetExtensionRequestState
    }
    const StateFilter: Component<StateFilterProps> = C => {
        return (
            <div
                class={`counter ${C.class || ''}`}
                role='button'
                onclick={() => {
                    if (state.state == C.state) setState('state', null)
                    else setState('state', C.state)

                    searchReqs()
                }}
                classList={{ active: C.state == state.state }}
            >
                <div class='icon'>{C.icon}</div>
                <p class='holder title_smaller'>{C.holder}</p>
            </div>
        )
    }

    const HelmetReqCmp: Component<REQ> = H => {
        return (
            <div
                class='helmet-req-cmp'
                role='button'
                onclick={() => {
                    setState('activeReq', deepcopy(H))
                }}
                classList={{
                    [H.state]: true,
                }}
            >
                <div class='req-head'>
                    <div class='head-right head-col'>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <CorpsIcon />
                                <span>شرکت:</span>
                            </div>
                            <div>{H.company?.name || 'بدون نام'}</div>
                        </div>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <UserIcon />
                                <span>کاربر:</span>
                            </div>
                            <div>{H.user?.name || 'بدون نام'}</div>
                        </div>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <ShieldCheckIcon />
                                <span>بیمه:</span>
                            </div>
                            <div>{H.helmet}</div>
                        </div>
                    </div>
                    <div class='head-left head-col'>
                        <div class='row title_smaller'>
                            {new Date(H.created_at * 1e3).toLocaleDateString(
                                'fa-IR'
                            )}
                            <div class='holder  icon'>
                                <CalendarIcon />
                            </div>
                        </div>
                        <div
                            class='row title_smaller'
                            classList={{ show: H.state !== 'pending' }}
                        >
                            <span>{HelmetState_FA[H.state]}</span>
                            <div
                                class='holder icon'
                                classList={{ [H.state]: true }}
                            >
                                <Show
                                    when={
                                        H.state !== 'rejected' &&
                                        H.state !== 'done'
                                    }
                                    fallback={
                                        <Show
                                            when={H.state == 'done'}
                                            fallback={<CloseIcon />}
                                        >
                                            <CheckFillIcon />
                                        </Show>
                                    }
                                >
                                    <div class='dot-pulse'></div>
                                </Show>
                            </div>
                        </div>
                    </div>
                </div>
                <div class='detail-container'>
                    <div class='req-detail title_smaller'>{H.detail}</div>
                </div>
            </div>
        )
    }

    return (
        <div class='requests-section section'>
            <div class='requests-wrapper'>
                <A class='extention-go-back icon' href='/panel/extentions'>
                    <ArrowLeftIcon />
                </A>
                <div class='head section_title'>
                    <EditIcon />
                    <span>درخواست ها</span>
                    <EditIcon />
                </div>
                <div
                    class='total-wrapper title_small'
                    classList={{ active: !state.loadingC }}
                >
                    <Counter end={state.count.total} />
                    <span>درخواست ثبت شده</span>
                </div>

                <div class='search-inps'>
                    <CorpPick />

                    <HoverInp
                        class='helmet-inp'
                        Icon={<CorpsIcon />}
                        holder='شماره بیمه'
                        acitve={state.helmet !== null}
                        inpType='number'
                        onInp={v => {
                            setState('helmet', parseInt(v || '0'))
                            searchReqs()
                        }}
                        value={String(state.helmet || '')}
                        showReset={state.helmet !== null}
                        onReset={() => {
                            setState('helmet', null)
                            searchReqs()
                        }}
                    />
                </div>
                <div class='state-filters'>
                    <StateFilter
                        state='done'
                        holder='حل شده'
                        icon={<CheckFillIcon />}
                        class='green'
                    />
                    <StateFilter
                        state='in_progress'
                        holder='درحال حل'
                        icon={<LoadingIcon />}
                    />
                    <StateFilter
                        state='pending'
                        holder='بدون وضعیت'
                        icon={<QuestionIcon2 />}
                        class='grey'
                    />
                    <StateFilter
                        state='rejected'
                        holder='رد شده'
                        icon={<CloseIcon />}
                        class='red'
                    />
                </div>

                <div class='reqs-wrapper'>
                    <Show when={!state.loadingR} fallback={<LoadingR />}>
                        <Show when={!state.error} fallback={<ErrorR />}>
                            <Show
                                when={state.reqs.length > 0}
                                fallback={<ErrorR empty />}
                            >
                                <For each={state.reqs}>
                                    {r => <HelmetReqCmp {...r} />}
                                </For>
                            </Show>
                        </Show>
                    </Show>
                </div>
            </div>

            <Show when={state.activeReq}>
                <ReqPopup />
            </Show>
        </div>
    )
}

const LoadingR = () => {
    return (
        <div class='reqs-loading title_hero'>
            <img src='/public/imgs/logo.png' decoding='async' alt='' />
            <div>در حال بارگذاری...</div>
        </div>
    )
}

const ErrorR: Component<{ empty?: boolean }> = P => {
    return (
        <div class='reqs-loading title_hero'>
            <CloseIcon />
            <div>
                <Show when={P.empty} fallback={'خطا در گرفتن اطلاعات'}>
                    درخواست ای با این مشخصات یافت نشد!
                </Show>
            </div>
        </div>
    )
}

const ReqPopup: Component = () => {
    type DATA = {
        loading: boolean
        error: boolean
    }
    const [data, setData] = createStore<DATA>({
        error: false,
        loading: false,
    })

    const close = () => {
        if (data.loading) return

        setState('activeReq', null)
    }

    const req = state.activeReq

    const submit = async (state: HelmetExtensionRequestState) => {
        setData('loading', true)

        let id = unwrap(req!.id)

        let res = await api_admin_helmet_extension_requests_patch(
            { id },
            {
                state,
            }
        )
        setData('loading', false)

        if (!res.ok()) return setData('error', true)

        addAlert({
            type: 'success',
            subject: 'موفق',
            content: 'نتیجه درخواست با موفقیت ثبت شد',
            timeout: 3,
        })

        setState(
            produce(s => {
                let index = s.reqs.findIndex(i => i.id == id)

                if (index !== -1) {
                    s.reqs[index]!.state = res.body.state
                }
            })
        )

        close()
    }

    const Loading = () => {
        return (
            <div class='req-loading title_hero'>
                <LoadingDots />

                <span>درحال ثبت الحاقیه</span>
            </div>
        )
    }

    const Ctas = () => {
        return (
            <>
                <button
                    type='button'
                    class='req-cta title_smaller red'
                    disabled={req?.state == 'rejected'}
                    onclick={() => {
                        setPopup({
                            show: true,
                            Icon: CloseIcon,
                            title: 'درخواست را رد میکنید؟',
                            type: 'error',
                            onSubmit: () => submit('rejected'),
                        })
                    }}
                >
                    <CloseIcon />
                    رد درخواست
                </button>
                <button
                    type='button'
                    class='req-cta title_smaller load'
                    disabled={req?.state == 'in_progress'}
                    onclick={() =>
                        setPopup({
                            show: true,
                            Icon: WarningIcon,
                            title: 'درخواست را به حالت انتظار میارید؟ ',
                            onSubmit: () => submit('in_progress'),
                            type: 'info',
                        })
                    }
                >
                    <LoadingIcon />
                    حالت انتظار
                </button>
                <button
                    type='submit'
                    disabled={req?.state == 'done'}
                    class='req-cta title_smaller check'
                >
                    <CheckFillIcon />
                    حل درخواست
                </button>
            </>
        )
    }

    return (
        <div
            class='helmet-req-popup'
            classList={{ active: !!state.activeReq }}
            onclick={close}
        >
            <form
                onsubmit={e => {
                    e.preventDefault()

                    setPopup({
                        show: true,
                        Icon: CheckFillIcon,
                        title: 'درخواست را تایید میکنید؟',
                        type: 'success',
                        onSubmit: () => submit('done'),
                    })
                }}
                onclick={e => e.stopPropagation()}
                class='helmet-req-wrapper'
            >
                <div class='close-req icon' role='button' onclick={close}>
                    <CloseIcon />
                </div>

                <div class='req-header title_smaller'>
                    <div class='header-col'>
                        <div class='icon'>
                            <CorpsIcon />
                        </div>
                        <span>{req?.company?.name || 'بدون نام'}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <UserIcon />
                        </div>
                        <span>{req?.user?.name || 'بدون نام'}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <ShieldCheckIcon />
                        </div>
                        <span>{req?.helmet}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <CalendarIcon />
                        </div>
                        <span>
                            {req &&
                                new Date(
                                    req.created_at * 1e3
                                ).toLocaleDateString('fa-IR')}
                        </span>
                    </div>
                </div>
                <div class='req-text-container'>
                    <div class='req-text'>
                        <div class='text-wrapper title_smaller'>
                            {req?.detail}
                        </div>
                    </div>
                </div>
                <div class='req-actions'>
                    <Ctas />
                </div>

                <Show when={data.loading}>
                    <Loading />
                </Show>
            </form>
        </div>
    )
}

export default ExtentionRequests
